import React from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

interface InputFieldProps {
  register?: UseFormRegisterReturn;
  label: string;
  type: string;
  name: string;
  value?: string | number;
  placeholder?: string;
  error?: string;
  step?: string | number;
  disable?: boolean,
  onBlur?: any,
  success?: string;
  className?: string
  autoComplete?: string
  readOnly?: boolean
}

const InputField: React.FC<InputFieldProps> = ({
  register,
  label,
  type,
  value,
  name,
  placeholder,
  error,
  disable,
  step,
  onBlur,
  success,
  className,
  autoComplete,
  readOnly
}) => {
  return (
    <div className={`mb-4 ${className ? className : ''}`}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        {...register}
        type={type}
        id={name}
        name={name}
        defaultValue={value}
        disabled={disable}
        placeholder={placeholder}
        step={step}
        onBlur={onBlur}
        autoComplete={autoComplete}
        readOnly={readOnly === true ? true : false}
        className={`w-full px-3 py-2 rounded-md border focus:outline-none
          ${error 
            ? 'border-red-500 text-red-900 placeholder-red-300 focus:ring-red-500' 
            : 'border-gray-300 focus:border-gray-600'
          }
          ${success 
            ? 'border-green-500 text-green-900 placeholder-green-300 focus:ring-green-500' 
            : 'border-gray-300 focus:border-gray-600'
          }
          ${type === 'date' ? 'pr-3' : 'pr-10'}
        `}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
      {success && (
        <p className="mt-1 text-sm text-green-600">
          {success}
        </p>
      )}
    </div>
  );
};


interface TimeInputFieldProps {
  label: string;
  name: string;
  placeholder?: string;
  register: any;
  error?: string;
  clearErrors?: any
}

const TimeInputField: React.FC<TimeInputFieldProps> = ({
  label,
  name,
  placeholder,
  register,
  error,
  clearErrors
}) => {
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 6) value = value.slice(0, 6);

    const formattedValue = value
      .replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2:$3')
      .replace(/(\d{2})(\d{2})/, '$1:$2');

      event.target.value = formattedValue;
      register.onChange(event);
      clearErrors && clearErrors()
  };

  return (
    <div className="mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        {...register}
        type="text"
        id={name}
        name={name}
        placeholder={placeholder}
        onChange={handleInputChange}
        className={`w-full px-3 py-2 rounded-md border focus:outline-none
          ${error 
            ? 'border-red-500 text-red-900 placeholder-red-300 focus:ring-red-500' 
            : 'border-gray-300 focus:border-gray-600'
          }
        `}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};


interface TextareaProps {
  register?: UseFormRegisterReturn;
  label: string;
  name: string;
  value?: string;
  placeholder?: string;
  error?: string;
  disable?: boolean;
  rows?: number;
}

const Textarea: React.FC<TextareaProps> = ({
  register,
  label,
  value,
  name,
  placeholder,
  error,
  disable,
  rows = 3
}) => {
  return (
    <div className="mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <textarea
        {...register}
        id={name}
        name={name}
        defaultValue={value}
        disabled={disable}
        placeholder={placeholder}
        rows={rows}
        className={`w-full px-3 py-2 min-h-28 rounded-md border focus:outline-none resize-vertical ${error ? 'border-red-500 text-red-900 placeholder-red-300 focus:ring-red-500' : 'border-gray-300 focus:border-gray-600'}`}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export {TimeInputField, Textarea};
export default InputField;
